// src/features/dashboard/components/EmployeeActiveHoursReport/ActiveHoursKpiCards.tsx

import {
  Clock3,
  Gauge,
  CalendarDays,
  Users,
  LucideIcon,
} from 'lucide-react';

import {
  ActiveHoursKpiResponse,
} from '../../types/EmployeeActiveHours/employeeActiveHours.types';

interface ActiveHoursCard {
  title: string;
  value: string;
  subtitle?: string;
  icon?: LucideIcon;
  bg?: string;
  color?: string;
}

interface Props {
  data: ActiveHoursKpiResponse;
}

/* ============================================================
   HELPERS
   ============================================================ */

/**
 * Converts API time values into decimal hours.
 *
 * Examples:
 *
 * "01:12:36"  -> 1.21
 * "08:34:12"  -> 8.57
 * "+00:00:00" -> 0
 * "-01:30:00" -> -1.50
 */
const timeToHours = (
  value: string | number | null | undefined,
): number => {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return 0;
  }

  /* ----------------------------------------------------------
     Number value
     ---------------------------------------------------------- */

  if (typeof value === 'number') {
    return Number.isFinite(value)
      ? value
      : 0;
  }

  let timeValue = String(value).trim();

  if (!timeValue) {
    return 0;
  }

  /* ----------------------------------------------------------
     Handle + / - sign
     ---------------------------------------------------------- */

  let sign = 1;

  if (timeValue.startsWith('+')) {
    timeValue = timeValue.substring(1);
  }

  if (timeValue.startsWith('-')) {
    sign = -1;
    timeValue = timeValue.substring(1);
  }

  /* ----------------------------------------------------------
     HH:mm:ss
     ---------------------------------------------------------- */

  if (timeValue.includes(':')) {
    const parts = timeValue
      .split(':')
      .map(Number);

    if (parts.length === 3) {
      const [
        hours,
        minutes,
        seconds,
      ] = parts;

      if (
        Number.isFinite(hours) &&
        Number.isFinite(minutes) &&
        Number.isFinite(seconds)
      ) {
        return sign * (
          hours +
          minutes / 60 +
          seconds / 3600
        );
      }

      return 0;
    }

    /* --------------------------------------------------------
       HH:mm
       -------------------------------------------------------- */

    if (parts.length === 2) {
      const [
        hours,
        minutes,
      ] = parts;

      if (
        Number.isFinite(hours) &&
        Number.isFinite(minutes)
      ) {
        return sign * (
          hours +
          minutes / 60
        );
      }

      return 0;
    }

    return 0;
  }

  /* ----------------------------------------------------------
     Numeric string
     ---------------------------------------------------------- */

  const numericValue = Number(timeValue);

  return Number.isFinite(numericValue)
    ? sign * numericValue
    : 0;
};

/**
 * Safely handles numbers.
 */
const safeNumber = (
  value: number,
): number => {
  return Number.isFinite(value)
    ? value
    : 0;
};

/**
 * Converts decimal hours into UI duration format.
 *
 * Examples:
 *
 * 7.20  -> "7 h 12 m"
 * 8.57  -> "8 h 34 m"
 * 1.21  -> "1 h 12 m"
 * 0     -> "0 h 0 m"
 * -1.50 -> "-1 h 30 m"
 *
 * This is only for display.
 */
const formatDuration = (
  decimalHours: number,
): string => {
  const safeHours = Number.isFinite(
    decimalHours,
  )
    ? decimalHours
    : 0;

  const sign =
    safeHours < 0
      ? '-'
      : '';

  const absoluteHours =
    Math.abs(safeHours);

  const hours =
    Math.floor(absoluteHours);

  const minutes = Math.round(
    (absoluteHours - hours) * 60,
  );

  /*
   * Example:
   *
   * 7.999 hours
   *
   * can become:
   *
   * 8 h 0 m
   *
   * instead of:
   *
   * 7 h 60 m
   */
  if (minutes === 60) {
    return `${sign}${hours + 1} h 0 m`;
  }

  return `${sign}${hours} h ${minutes} m`;
};

/* ============================================================
   KPI CARD
   ============================================================ */

const KpiCard = ({
  card,
}: {
  card: ActiveHoursCard;
}) => {
  const Icon = card.icon;

  return (
    <div
      className="
        flex
        min-w-0
        w-full
        items-center
        gap-3
        rounded-[16px]
        border
        border-[#D0D5DD]
        bg-white
        px-3
        py-3
        h-[80px]
      "
    >
      {/* ======================================================
          ICON
          ====================================================== */}

      <div
        className={`
          flex
          h-[32px]
          w-[32px]
          shrink-0
          items-center
          justify-center
          rounded-full
          ${card.bg ?? 'bg-[#F2F4F7]'}
        `}
      >
        {Icon && (
          <Icon
            size={16}
            strokeWidth={2.5}
            className={
              card.color ??
              'text-[#667085]'
            }
          />
        )}
      </div>

      {/* ======================================================
          CONTENT
          ====================================================== */}

      <div className="min-w-0 flex-1">
        {/* Title */}

        <h4
          className="
            truncate
            text-[12px]
            font-semibold
            leading-[16px]
            text-[#101828]
          "
        >
          {card.title}
        </h4>

        {/* Value */}

        <div
          className="
            mt-[2px]
            flex
            min-w-0
            items-end
          "
        >
          <span
            className="
              truncate
              text-[16px]
              font-bold
              leading-[20px]
              text-[#101828]
            "
          >
            {card.value}
          </span>
        </div>

        {/* Subtitle */}

        {card.subtitle && (
          <p
            className={`
              mt-[2px]
              truncate
              text-[11px]
              leading-[14px]
              ${
                card.title === 'Active Today' ||
                card.title === 'vs Team Avg'
                  ? 'font-medium text-[#16A34A]'
                  : 'text-[#667085]'
              }
            `}
            title={card.subtitle}
          >
            {card.subtitle}
          </p>
        )}
      </div>
    </div>
  );
};

/* ============================================================
   MAIN COMPONENT
   ============================================================ */

const ActiveHoursKpiCards = ({
  data,
}: Props) => {
  /* ==========================================================
     NORMALIZE API VALUES
     ========================================================== */

  /*
   * API:
   *
   * activeToday: "01:12:36"
   * utilization: 99.180327
   * sevenDayAvg: "08:34:12"
   * vsTeamAvg: "+00:00:00"
   */

  const activeToday =
    safeNumber(
      timeToHours(
        data?.activeToday,
      ),
    );

  const utilization =
    safeNumber(
      Number(
        data?.utilization ?? 0,
      ),
    );

  const sevenDayAvg =
    safeNumber(
      timeToHours(
        data?.sevenDayAvg,
      ),
    );

  const vsTeamAvg =
    safeNumber(
      timeToHours(
        data?.vsTeamAvg,
      ),
    );

  /* ==========================================================
     ACTIVE TODAY VS 7 DAY AVG
     ========================================================== */

  const activeTodayPercentage =
    sevenDayAvg > 0
      ? Math.abs(
          (
            (activeToday -
              sevenDayAvg) /
            sevenDayAvg
          ) * 100,
        )
      : 0;

  /* ==========================================================
     ABOVE / BELOW
     ========================================================== */

  const activeTodayStatus =
    activeToday >= sevenDayAvg
      ? 'above'
      : 'below';

  const teamStatus =
    vsTeamAvg >= 0
      ? 'Above'
      : 'Below';

  /* ==========================================================
     KPI CARDS
     ========================================================== */

  const cards: ActiveHoursCard[] = [
    /* --------------------------------------------------------
       ACTIVE TODAY
       -------------------------------------------------------- */

    {
      title: 'Active Today',

      value:
        formatDuration(
          activeToday,
        ),

      subtitle:
        `${activeTodayPercentage.toFixed(0)}% ${activeTodayStatus} 7d avg`,

      icon: Clock3,

      bg: 'bg-[#EAF2FF]',

      color: 'text-[#2563EB]',
    },

    /* --------------------------------------------------------
       UTILIZATION
       -------------------------------------------------------- */

    {
      title: 'Utilization',

      value:
        `${utilization.toFixed(0)}%`,

      subtitle:
        'Active / Logged time',

      icon: Gauge,

      bg: 'bg-[#ECFDF3]',

      color: 'text-[#16A34A]',
    },

    /* --------------------------------------------------------
       7 DAY AVG
       -------------------------------------------------------- */

    {
      title: '7 Day Avg',

      value:
        formatDuration(
          sevenDayAvg,
        ),

      subtitle:
        'Rolling 7 working days',

      icon: CalendarDays,

      bg: 'bg-[#FFF4E5]',

      color: 'text-[#F59E0B]',
    },

    /* --------------------------------------------------------
       VS TEAM AVG
       -------------------------------------------------------- */

    {
      title: 'vs Team Avg',

      value:
        formatDuration(
          vsTeamAvg,
        ),

      subtitle:
        `${teamStatus} team avg today`,

      icon: Users,

      bg: 'bg-[#FEF3F2]',

      color: 'text-[#DC2626]',
    },
  ];

  /* ==========================================================
     DYNAMIC DESKTOP COLUMNS
     ========================================================== */

  const desktopColumns =
    `repeat(${cards.length}, minmax(0, 1fr))`;

  /* ==========================================================
     RENDER
     ========================================================== */

  return (
    <div className="w-full">
      {/* ======================================================
          MOBILE
          ====================================================== */}

      <div
        className="
          grid
          grid-cols-1
          gap-4
          sm:grid-cols-2
          lg:hidden
        "
      >
        {cards.map((card) => (
          <KpiCard
            key={card.title}
            card={card}
          />
        ))}
      </div>

      {/* ======================================================
          DESKTOP
          ====================================================== */}

      <div
        className="
          hidden
          w-full
          gap-4
          lg:grid
        "
        style={{
          gridTemplateColumns:
            desktopColumns,
        }}
      >
        {cards.map((card) => (
          <KpiCard
            key={card.title}
            card={card}
          />
        ))}
      </div>
    </div>
  );
};

export default ActiveHoursKpiCards;